// INV-4: one App owns each context; effect replay preserves it, final unmount closes it, and explicit close joins teardown.
import { useSpacesView } from '../db/spaces-queries'
import { useKeyboardShortcut, type ShortcutEvent } from '@w/utils-fe'
import { LoadingIndicator } from '@w/ui/components/loading-indicator'
import { IconButton } from '@w/ui/components/icon-button'
import {
  ActivityIcon,
  BugIcon,
  DatabaseIcon,
  ServerIcon,
  MoonIcon,
  SunIcon,
  XIcon,
} from '@w/ui/components/icon'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@w/ui/components/dialog'
import { NavBar, NavBarItem } from '@w/ui/components/nav-bar'
import { useTheme } from '@w/ui'
import { ErrorBoundary } from '@w/ui/system/error-boundary'
import { ThemeProvider } from '@w/ui/components/theme-provider'
import { LocalStorageProvider } from '@w/utils-fe'
import { I18nProvider } from '../i18n'
import { getThemeStorageKey } from './theme-storage'
import React, {
  Suspense,
  createContext,
  use,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import { useTranslation } from '../i18n'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import type { SpacesResources } from '../router/spaces-resources'
import { useSession } from '@w/auth-fe'
// Small browser-only helper; the explorer itself stays lazy.
import { readHashParam, writeHashParams } from '@w/tel-ui/hash-link'

const TelemetryExplorer = React.lazy(() =>
  import('./telemetry-explorer').then((module) => ({
    default: module.FlashcardsTelemetryExplorer,
  })),
)
const DatabaseInspector = React.lazy(() =>
  import('./database-inspector').then((module) => ({
    default: module.DatabaseInspector,
  })),
)
const RuntimeInspector = React.lazy(() =>
  import('@w/runtime/inspector').then(module => ({ default: module.RuntimeInspector })),
)

const SignOutContext = createContext<(() => Promise<void>) | null>(null)
export function useSignOut() {
  const signOut = use(SignOutContext)
  if (!signOut) throw new Error('Sign out requires App')
  return signOut
}

export function App(props: PropsWithChildren) {
  const ctx = useRouterContext()
  const [signingOut, setSigningOut] = useState(false)
  const [signOut] = useState(() => async () => {
    setSigningOut(true)
    // The authentication service decides whether the session has been revoked.
    try { await ctx.auth.signOut() }
    finally { setSigningOut(false) }
  })
  const pendingDisposal = useRef(new Set<typeof ctx>())
  useEffect(() => {
    const pending = pendingDisposal.current
    pending.delete(ctx)
    return () => {
      pending.add(ctx)
      queueMicrotask(() => { if (pending.delete(ctx)) void ctx.close() })
    }
  }, [ctx])
  return (
      <SignOutContext value={signOut}>
      {signingOut ? <p role="status">Signing out…</p> : null}
      <div inert={signingOut} data-testid="flashcards-write-surface">
      <ErrorBoundary
        onError={(error) => {
          ctx.tel.error(error, { operation: 'react.render' })
        }}
      >
        {props.children}
      </ErrorBoundary>
      <ThemeShortcut />
      </div>
      </SignOutContext>
  )
}

function ThemeShortcut() {
  const { theme, setTheme } = useTheme()

  const handleKeyDown = (event: ShortcutEvent) => {
    if (
      event.defaultPrevented ||
      !event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.key.toLowerCase() !== 't'
    ) {
      return
    }
    event.preventDefault()
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  useKeyboardShortcut({ t: handleKeyDown })

  return null
}

export function FlashcardsDevtools() {
  const { auth, localStorage } = useRouterContext()
  return (
    <LocalStorageProvider storage={localStorage}>
      <I18nProvider>
        <ThemeProvider storageKey={getThemeStorageKey(auth.getUserOrNull()?.id)}>
          <DebugToolsButton />
        </ThemeProvider>
      </I18nProvider>
    </LocalStorageProvider>
  )
}

const debugViews = ['telemetry', 'database', 'runtime']

/** The URL hash key `debug=<view>` reopens the panel on reload; the explorer keeps its own `tel` key. */
function DebugToolsButton() {
  const [open, setOpen] = useState(() => debugViews.includes(readHashParam('debug') ?? ''))
  const [view, setView] = useState(() => debugViews.find(value => value === readHashParam('debug')) ?? 'telemetry')
  const show = (nextView: string) => {
    setOpen(true)
    setView(nextView)
    writeHashParams({ debug: nextView })
  }
  const close = () => {
    setOpen(false)
    writeHashParams({ debug: undefined, tel: undefined })
  }
  const { t } = useTranslation('shell')

  const handleKeyDown = (event: ShortcutEvent) => {
    if (
      event.defaultPrevented ||
      !event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      !event.shiftKey ||
      event.key.toLowerCase() !== 't'
    ) {
      return
    }
    event.preventDefault()
    show(view)
  }

  useKeyboardShortcut({ t: handleKeyDown })

  return (
    <>
      <IconButton
        aria-label={t('debugTools')}
        className="bg-surface-2 border-separator fixed right-3 bottom-3 z-50 size-9 rounded-lg border shadow-sm"
        data-testid="flashcards-debug-tools"
        semanticRole="secondary"
        title={t('debugToolsShortcutHint', {
          action: t('debugTools'),
          shortcut: t('debugToolsShortcut'),
        })}
        type="button"
        variant="text"
        onClick={() => {
          show(view)
        }}
      >
        <BugIcon />
        {t('debugTools')}
      </IconButton>
      {open && (
        <Dialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) close()
          }}
        >
          <DialogContent
            aria-label={t('debugTools')}
            className="h-dvh max-h-dvh w-screen min-w-0 max-w-none overflow-hidden rounded-none"
          >
            <DialogTitle className="sr-only">{t('debugTools')}</DialogTitle>
            <div className="relative flex min-h-0 flex-1 flex-col pb-20">
              <header className="border-separator flex h-14 shrink-0 items-center justify-between gap-2 border-b px-4">
                <div className="flex min-w-0 items-center gap-2">
                  <BugIcon className="size-5" />
                  <span className="text-label-large truncate font-semibold">
                    {t('debugTools')}
                  </span>
                </div>
                <DialogClose render={<IconButton size="sm" variant="text" />}>
                  <XIcon />
                  {t('debugClose')}
                </DialogClose>
              </header>
              {view === 'telemetry' ? (
                <div
                  className="flex min-h-0 flex-1 overflow-hidden"
                  role="tabpanel"
                >
                  <Suspense fallback={<div className="flex flex-1 items-center justify-center p-4"><LoadingIndicator /></div>}>
                    <TelemetryExplorer />
                  </Suspense>
                </div>
              ) : view === 'runtime' ? (
                <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden" role="tabpanel">
                  <Suspense fallback={<div className="p-4">Loading runtime inspector…</div>}>
                    <RuntimeInspector />
                  </Suspense>
                </div>
              ) : (
                <div
                  className="flex min-h-0 flex-1 overflow-hidden"
                  role="tabpanel"
                >
                  <Suspense fallback={null}>
                    <DatabaseDebugView />
                  </Suspense>
                </div>
              )}
              <NavBar
                aria-label={t('debugViews')}
                autoHide={false}
                className="absolute border-t"
                value={view}
                onValueChange={show}
              >
                <NavBarItem
                  data-testid="flashcards-debug-telemetry"
                  icon={<ActivityIcon />}
                  value="telemetry"
                >
                  {t('telemetry')}
                </NavBarItem>
                <NavBarItem
                  data-testid="flashcards-debug-database"
                  icon={<DatabaseIcon />}
                  value="database"
                >
                  {t('database')}
                </NavBarItem>
                <NavBarItem
                  data-testid="flashcards-debug-runtime"
                  icon={<ServerIcon />}
                  value="runtime"
                >
                  {t('runtime')}
                </NavBarItem>
              </NavBar>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

function DatabaseDebugView() {
  const ctx = useRouterContext()
  const { t } = useTranslation('shell')
  // Outside the protected routes: read the session snapshot, never the auth-validating getter.
  const spaces = useSession(ctx.session)
  return spaces ? <UserDatabaseDebugView spaces={spaces} /> : <div>{t('databaseUnavailable')}</div>
}

function UserDatabaseDebugView({ spaces }: { spaces: SpacesResources }) {
  const ctx = useRouterContext()
  const { t } = useTranslation('shell')
  const { data } = useSpacesView(spaces, spaces.userId, ctx.queryClient)
  const driver = data?.resources.find(resource => resource.space.kind === 'private')?.db.driver

  return driver ? (
    <DatabaseInspector driver={driver} tel={ctx.tel} />
  ) : (
    <div className="text-on-surface-secondary flex flex-1 items-center justify-center p-6 text-center">
      {t('databaseUnavailable')}
    </div>
  )
}

export function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const { t } = useTranslation('shell')
  const nextTheme = theme === 'dark' ? 'light' : 'dark'
  const action = nextTheme === 'dark' ? t('switchToDark') : t('switchToLight')

  function toggleTheme() {
    setTheme(nextTheme)
  }

  return (
    <IconButton
      aria-label={action}
      className="rounded-lg"
      data-testid="flashcards-theme-toggle"
      semanticRole="secondary"
      size="sm"
      title={t('themeShortcutHint', {
        action,
        shortcut: t('themeShortcut'),
      })}
      type="button"
      variant="text"
      onClick={toggleTheme}
    >
      {nextTheme === 'dark' ? <MoonIcon /> : <SunIcon />}
      {action}
    </IconButton>
  )
}
